<template>
  <div>
    <b-form @submit.stop.prevent="onSubmit">
      <b-card
        class="box-shadow2 rounded p-3"
        :bg-variant="isDarkMode ? 'dark' : 'light'"
        :text-variant="isDarkMode ? 'light' : 'dark'"
      >
        <b-form-group label-for="dd-key">
          <template #label><span>Key</span> <span class="text-danger">*</span></template>
          <b-form-input
            id="dd-key"
            v-model="form.key"
            :disabled="formType === 'update'"
            required
            :class="inputClass"
            placeholder="unique key (stored lowercased)"
            trim
          />
        </b-form-group>
        <b-form-group>
          <b-form-checkbox v-model="form.isActive" :class="isDarkMode ? 'text-light' : ''"> Active </b-form-checkbox>
        </b-form-group>

        <div class="d-flex justify-content-between align-items-center mb-2">
          <strong>Categories</strong>
          <b-button
            size="sm"
            class="box-shadow2"
            :variant="isDarkMode ? 'outline-light' : 'outline-primary'"
            type="button"
            @click="addCategory"
          >
            Add category
          </b-button>
        </div>

        <b-card
          v-for="(cat, ci) in form.categories"
          :key="`cat-${ci}`"
          class="mb-3 box-shadow2"
          :bg-variant="isDarkMode ? 'secondary' : 'light'"
          :text-variant="isDarkMode ? 'light' : 'dark'"
        >
          <div class="d-flex justify-content-between mb-2">
            <span>Category {{ ci + 1 }}</span>
            <b-button size="sm" class="box-shadow2" variant="outline-danger" type="button" @click="removeCategory(ci)">
              Remove
            </b-button>
          </div>
          <b-row>
            <b-col md="4">
              <b-form-group label="Label">
                <b-form-input v-model="cat.label" :class="inputClass" required trim />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Order">
                <b-form-input v-model.number="cat.order" type="number" :class="inputClass" />
              </b-form-group>
            </b-col>
            <b-col md="4" class="d-flex align-items-center">
              <b-form-checkbox v-model="cat.isActive" :class="isDarkMode ? 'text-light' : ''"> Active </b-form-checkbox>
            </b-col>
          </b-row>
          <div class="d-flex justify-content-between align-items-center mb-1">
            <small>Options</small>
            <b-button size="sm" class="box-shadow2" variant="outline-secondary" type="button" @click="addOption(ci)">
              Add option
            </b-button>
          </div>
          <div
            v-for="(opt, oi) in cat.options"
            :key="`opt-${ci}-${oi}`"
            class="mb-2 border rounded p-2 box-shadow3"
            :class="{ 'border-secondary': isDarkMode }"
          >
            <b-row>
              <b-col md="5">
                <b-form-group label="Label" class="mb-1">
                  <b-form-input v-model="opt.label" :class="inputClass" required trim />
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Value (lowercase)" class="mb-1">
                  <b-form-input v-model="opt.value" :class="inputClass" required trim />
                </b-form-group>
              </b-col>
              <b-col md="2">
                <b-form-group label="Order" class="mb-1">
                  <b-form-input v-model.number="opt.order" type="number" :class="inputClass" />
                </b-form-group>
              </b-col>
              <b-col md="1" class="d-flex align-items-end">
                <b-button size="sm" variant="link" class="text-danger p-0" type="button" @click="removeOption(ci, oi)">
                  ×
                </b-button>
              </b-col>
            </b-row>
            <b-form-checkbox v-model="opt.isActive" :class="isDarkMode ? 'text-light' : ''"> Active </b-form-checkbox>
          </div>
        </b-card>

        <div class="text-right">
              <b-button type="submit" :variant="isDarkMode ? 'primary' : 'success'" class="mr-2 box-shadow2">{{
            submitLabel
          }}</b-button>
              <b-button
                type="button"
                class="box-shadow2"
                :variant="isDarkMode ? 'outline-light' : 'outline-secondary'"
                @click="onCancel"
              >
            Cancel
          </b-button>
        </div>
      </b-card>
    </b-form>
  </div>
</template>

<script>
import { mapState } from 'vuex';

function emptyOption() {
  return { label: '', value: '', order: 0, isActive: true };
}

function emptyCategory() {
  return { label: '', order: 0, isActive: true, options: [emptyOption()] };
}

export default {
  name: 'DropdownFormBox',
  props: {
    formType: {
      type: String,
      required: true
    },
    initial: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      form: {
        key: '',
        isActive: true,
        categories: []
      },
      originalKey: ''
    };
  },
  computed: {
    ...mapState('darkMode', ['isDarkMode']),
    inputClass() {
      return this.isDarkMode ? 'bg-dark text-light placeholder-light' : 'box-shadow3 bg-light text-dark';
    },
    submitLabel() {
      return this.formType === 'new' ? 'Create' : 'Save';
    }
  },
  watch: {
    initial: {
      deep: true,
      handler(val) {
        this.applyInitial(val);
      }
    }
  },
  mounted() {
    this.applyInitial(this.initial);
  },
  methods: {
    applyInitial(val) {
      if (this.formType === 'new' || !val) {
        this.form = {
          key: '',
          isActive: true,
          categories: []
        };
        this.originalKey = '';
        return;
      }
      this.originalKey = val.key || '';
      const cats = Array.isArray(val.categories) ? JSON.parse(JSON.stringify(val.categories)) : [];
      cats.forEach(c => {
        if (!Array.isArray(c.options)) c.options = [];
      });
      this.form = {
        key: val.key || '',
        isActive: val.isActive !== false,
        categories: cats.length ? cats : []
      };
    },
    addCategory() {
      this.form.categories.push(emptyCategory());
    },
    removeCategory(index) {
      this.form.categories.splice(index, 1);
    },
    addOption(catIndex) {
      if (!this.form.categories[catIndex].options) {
        this.$set(this.form.categories[catIndex], 'options', []);
      }
      this.form.categories[catIndex].options.push(emptyOption());
    },
    removeOption(catIndex, optIndex) {
      this.form.categories[catIndex].options.splice(optIndex, 1);
    },
    buildPayload() {
      const key = String(this.form.key || '')
        .trim()
        .toLowerCase();
      const categories = this.form.categories.map((c, idx) => ({
        label: String(c.label || '').trim(),
        order: Number.isFinite(Number(c.order)) ? Number(c.order) : idx,
        isActive: c.isActive !== false,
        options: (c.options || []).map((o, j) => ({
          label: String(o.label || '').trim(),
          value: String(o.value || '')
            .trim()
            .toLowerCase(),
          order: Number.isFinite(Number(o.order)) ? Number(o.order) : j,
          isActive: o.isActive !== false
        }))
      }));
      return {
        key,
        isActive: !!this.form.isActive,
        categories
      };
    },
    onSubmit() {
      const payload = this.buildPayload();
      if (this.formType === 'new') {
        this.$emit('create', { payload });
      } else {
        this.$emit('update', { payload: { ...payload, originalKey: this.originalKey } });
      }
    },
    onCancel() {
      this.$router.push('/dropdowns-admin');
    }
  }
};
</script>
